// Formül ifade alanı için yazarken kod tamamlama — SAF yardımcılar.
// Tarayıcı `datalist`'i tüm input değerini eşlediği için ikinci koddan itibaren öneri vermiyordu
// (kullanıcı bulgusu, 2026-10-08). Burada imlecin üzerinde durduğu JETON bulunur ve yalnız o
// jeton için öneri üretilir; kabul edilince yalnız jeton değişir.

export interface CursorToken {
  start: number;
  end: number;
  word: string;
}

export interface CodeSuggestion {
  kind: "code" | "function" | "dimension";
  /** eklenecek metin (fonksiyonda açık parantezle: `TOPLA(`) */
  insert: string;
  /** listede görünen ad */
  code: string;
  label?: string;
  unit?: string | null;
}

export const FORMULA_FUNCTIONS: readonly string[] = [
  "TOPLA",
  "TOPLA_ALT",
  "ORT",
  "ORT_ALT",
  "AGIRLIKLI_ORT",
  "ONCEKI_YIL",
  "DEGISIM_YUZDE",
  "CEVIR",
];

const IDENT = /[A-Za-z0-9_çğıöşüÇĞİÖŞÜ]/;

/** İmlecin içinde/sonunda olduğu tanımlayıcı jetonu; imleç boşluktaysa null. */
export function tokenAtCursor(expression: string, cursor: number): CursorToken | null {
  const at = Math.max(0, Math.min(cursor, expression.length));
  let start = at;
  while (start > 0 && IDENT.test(expression[start - 1]!)) start -= 1;
  let end = at;
  while (end < expression.length && IDENT.test(expression[end]!)) end += 1;
  if (start === end) return null;
  // Sayı sabiti (123, 1_000) kod adayı değildir
  const word = expression.slice(start, end);
  if (/^[0-9]/.test(word)) return null;
  return { start, end, word };
}

function fold(s: string): string {
  return s.toLocaleLowerCase("tr-TR").replace(/ı/g, "i");
}

/**
 * Öneri sırası: kod öneki → etiket öneki → kod/etiket içinde geçen. Fonksiyon adları yalnız jeton
 * BÜYÜK harfle başlıyorsa (TOPLA… gibi) listeye girer. Boş jeton öneri üretmez.
 */
export function suggestCodes(
  word: string,
  codes: Iterable<string>,
  opts: {
    labelsByCode?: ReadonlyMap<string, string> | undefined;
    unitsByCode?: ReadonlyMap<string, string | null> | undefined;
    exclude?: string | undefined;
    limit?: number | undefined;
  } = {},
): CodeSuggestion[] {
  const w = fold(word.trim());
  if (w === "") return [];
  const limit = opts.limit ?? 8;
  const tiers: CodeSuggestion[][] = [[], [], []];
  for (const code of codes) {
    if (code === opts.exclude) continue;
    const label = opts.labelsByCode?.get(code);
    const unit = opts.unitsByCode?.get(code);
    const item: CodeSuggestion = {
      kind: "code",
      insert: code,
      code,
      ...(label !== undefined ? { label } : {}),
      ...(unit !== undefined ? { unit } : {}),
    };
    const c = fold(code);
    const l = label !== undefined ? fold(label) : "";
    if (c.startsWith(w)) tiers[0]!.push(item);
    else if (l !== "" && l.split(/\s+/).some((p) => p.startsWith(w))) tiers[1]!.push(item);
    else if (c.includes(w) || l.includes(w)) tiers[2]!.push(item);
  }
  const out = tiers.flat().slice(0, limit);
  if (/^[A-ZÇĞİÖŞÜ]/.test(word)) {
    const fns = FORMULA_FUNCTIONS.filter((f) => f.startsWith(word.toUpperCase())).map(
      (f): CodeSuggestion => ({ kind: "function", insert: `${f}(`, code: f }),
    );
    return [...fns, ...out].slice(0, limit);
  }
  return out;
}

/**
 * İmleç `TOPLA_ALT("…` / `ORT_ALT("…` grup argümanının açık tırnağı içindeyse yazılan boyut parçası
 * (boş olabilir); değilse null. Kabul `applySuggestion` ile yalnız bu parçayı değiştirir.
 */
export function dimensionArgAtCursor(expression: string, cursor: number): CursorToken | null {
  const at = Math.max(0, Math.min(cursor, expression.length));
  const m = /(?:TOPLA_ALT|ORT_ALT)\(\s*"([a-z0-9_]*)$/.exec(expression.slice(0, at));
  if (!m) return null;
  let end = at;
  while (end < expression.length && /[a-z0-9_]/.test(expression[end]!)) end += 1;
  const start = at - m[1]!.length;
  return { start, end, word: expression.slice(start, end) };
}

/** Grup argümanı önerisi: boyut slug'ı öneki ya da etiket kelime öneki (Türkçe katlamalı). */
export function suggestDimensions(
  word: string,
  dimensions: readonly string[],
  labels: Readonly<Record<string, string>> = {},
  limit = 8,
): CodeSuggestion[] {
  const w = fold(word.trim());
  return dimensions
    .filter((d) => {
      if (w === "") return true;
      const l = labels[d] !== undefined ? fold(labels[d]) : "";
      return fold(d).startsWith(w) || l.split(/\s+/).some((p) => p.startsWith(w));
    })
    .slice(0, limit)
    .map((d) => ({
      kind: "dimension",
      insert: d,
      code: d,
      ...(labels[d] !== undefined ? { label: labels[d] } : {}),
    }));
}

/** Jetonu öneriyle değiştirir; imleç eklenen metnin sonuna gelir. */
export function applySuggestion(
  expression: string,
  token: CursorToken,
  insert: string,
): { expression: string; cursor: number } {
  const next = expression.slice(0, token.start) + insert + expression.slice(token.end);
  return { expression: next, cursor: token.start + insert.length };
}
